import { useState, useEffect } from 'react';
import {
  Users,
  Plus,
  X,
  Loader2,
  AlertCircle,
  CheckCircle,
  Shield,
  ShieldCheck,
  ChevronDown,
  ChevronRight,
  Trash2,
  Save,
  Eye,
  EyeOff,
  Mail,
  User,
  Crown,
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { userApi, type TenantUser } from '../lib/api';
import { EmptyState } from '../components/EmptyState';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { Select } from '../components/ui/Select';

// ─── Role translations and colors ─────────────────────────────
const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Właściciel',
  ADMIN: 'Administrator',
  MANAGER: 'Kierownik',
  MARKETER: 'Marketer',
  WAREHOUSE: 'Magazynier',
  ACCOUNTANT: 'Księgowy',
  EMPLOYEE: 'Pracownik',
};

function getRoleLabel(role: string, isOwner: boolean): string {
  if (isOwner) return 'Właściciel';
  return ROLE_LABELS[role] || role;
}

// ─── Permission tree definition ──────────────────────────────
// Structure: module → category → permission
// deniedPermissions stores DENIED items. Empty array = full access.

interface PermissionItem {
  key: string;
  label: string;
}

interface PermissionCategory {
  key: string;
  label: string;
  items: PermissionItem[];
}

interface PermissionModule {
  key: string;
  label: string;
  categories: PermissionCategory[];
}

const PERMISSION_TREE: PermissionModule[] = [
  {
    key: 'management',
    label: 'Zarządzanie',
    categories: [
      {
        key: 'orders',
        label: 'Zamówienia',
        items: [
          { key: 'orders.view', label: 'Przeglądanie zamówień' },
          { key: 'orders.create', label: 'Tworzenie zamówień ręcznych' },
          { key: 'orders.edit', label: 'Edycja zamówień' },
          { key: 'orders.delete', label: 'Usuwanie zamówień' },
          { key: 'orders.status_change', label: 'Zmiana statusu' },
          { key: 'orders.bulk_status', label: 'Zbiorcza zmiana statusu' },
          { key: 'orders.notes', label: 'Notatki wewnętrzne' },
          { key: 'orders.email', label: 'Wysyłanie e-maili' },
          { key: 'orders.history', label: 'Historia zmian' },
        ],
      },
      {
        key: 'shipments',
        label: 'Przesyłki',
        items: [
          { key: 'shipments.create', label: 'Tworzenie przesyłek' },
          { key: 'shipments.bulk_create', label: 'Zbiorcze tworzenie przesyłek' },
          { key: 'shipments.labels', label: 'Pobieranie etykiet' },
          { key: 'shipments.cancel', label: 'Anulowanie przesyłek' },
        ],
      },
      {
        key: 'invoices',
        label: 'Faktury',
        items: [
          { key: 'invoices.create', label: 'Tworzenie faktur' },
          { key: 'invoices.bulk_create', label: 'Zbiorcze tworzenie faktur' },
          { key: 'invoices.download', label: 'Pobieranie PDF' },
          { key: 'invoices.delete', label: 'Usuwanie faktur' },
          { key: 'invoices.corrections', label: 'Korekty faktur' },
          { key: 'invoices.ksef', label: 'Wysyłka do KSeF' },
        ],
      },
      {
        key: 'refunds',
        label: 'Zwroty',
        items: [
          { key: 'refunds.view', label: 'Przeglądanie zwrotów' },
          { key: 'refunds.create', label: 'Tworzenie zwrotów' },
          { key: 'refunds.config', label: 'Konfiguracja limitów zwrotów' },
        ],
      },
      {
        key: 'warehouse',
        label: 'Magazyn',
        items: [
          { key: 'warehouse.view', label: 'Przeglądanie produktów' },
          { key: 'warehouse.create', label: 'Dodawanie produktów' },
          { key: 'warehouse.edit', label: 'Edycja produktów' },
          { key: 'warehouse.delete', label: 'Usuwanie produktów' },
          { key: 'warehouse.adjust_stock', label: 'Korekty stanów magazynowych' },
          { key: 'warehouse.import_export', label: 'Import/eksport produktów' },
        ],
      },
      {
        key: 'stores',
        label: 'Sklepy',
        items: [
          { key: 'stores.view', label: 'Przeglądanie sklepów' },
          { key: 'stores.create', label: 'Dodawanie sklepów' },
          { key: 'stores.edit', label: 'Edycja sklepów' },
          { key: 'stores.delete', label: 'Usuwanie sklepów' },
          { key: 'stores.sync', label: 'Synchronizacja zamówień' },
        ],
      },
      {
        key: 'automations',
        label: 'Automatyzacje',
        items: [
          { key: 'automations.view', label: 'Przeglądanie automatyzacji' },
          { key: 'automations.create_edit', label: 'Tworzenie i edycja' },
          { key: 'automations.delete', label: 'Usuwanie automatyzacji' },
          { key: 'automations.execute', label: 'Ręczne uruchamianie' },
        ],
      },
      {
        key: 'statuses',
        label: 'Statusy zamówień',
        items: [
          { key: 'statuses.view', label: 'Przeglądanie statusów' },
          { key: 'statuses.create_edit', label: 'Tworzenie i edycja' },
          { key: 'statuses.delete', label: 'Usuwanie statusów' },
        ],
      },
      {
        key: 'settings',
        label: 'Ustawienia',
        items: [
          { key: 'settings.general', label: 'Ustawienia ogólne' },
          { key: 'settings.sync', label: 'Ustawienia synchronizacji' },
          { key: 'settings.warehouse', label: 'Ustawienia magazynu' },
          { key: 'settings.email', label: 'Konfiguracja e-mail/SMTP' },
          { key: 'settings.couriers', label: 'Konfiguracja kurierów' },
          { key: 'settings.invoices', label: 'Konfiguracja faktur' },
          { key: 'settings.payments', label: 'Konfiguracja płatności' },
        ],
      },
    ],
  },
  {
    key: 'marketing',
    label: 'Marketing',
    categories: [
      {
        key: 'content',
        label: 'Treści i wysyłki',
        items: [
          { key: 'marketing.view', label: 'Przeglądanie Marketingu' },
          { key: 'marketing.create', label: 'Tworzenie i edycja materiałów' },
          { key: 'marketing.generate', label: 'Generowanie AI' },
          { key: 'marketing.test', label: 'Testy i podglądy' },
          { key: 'marketing.publish_low_risk', label: 'Publikacja niskiego ryzyka' },
        ],
      },
      {
        key: 'governance',
        label: 'Ustawienia i compliance',
        items: [
          { key: 'marketing.provider', label: 'Konfiguracja providera' },
          { key: 'marketing.consent_rules', label: 'Reguły zgód' },
          { key: 'marketing.system_limits', label: 'Limity systemowe' },
          { key: 'marketing.approve_high_risk', label: 'Approval wysokiego ryzyka' },
        ],
      },
    ],
  },
];

const ASSIGNABLE_ROLES = [
  { value: 'ADMIN', label: 'Administrator' },
  { value: 'MANAGER', label: 'Kierownik' },
  { value: 'MARKETER', label: 'Marketer' },
  { value: 'WAREHOUSE', label: 'Magazynier' },
  { value: 'ACCOUNTANT', label: 'Księgowy' },
  { value: 'EMPLOYEE', label: 'Pracownik' },
] as const;

// Get all item keys within a category
function getCategoryItemKeys(category: PermissionCategory): string[] {
  return category.items.map(i => i.key);
}

// Get all item keys within a module
function getModuleItemKeys(module: PermissionModule): string[] {
  return module.categories.flatMap(c => c.items.map(i => i.key));
}

// ─── Permission Tree Component ───────────────────────────────
function PermissionTreeEditor({
  deniedPermissions,
  onChange,
  disabled,
}: {
  deniedPermissions: string[];
  onChange: (denied: string[]) => void;
  disabled?: boolean;
}) {
  const [expandedModules, setExpandedModules] = useState<string[]>(['management']);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);

  const isDenied = (key: string) => deniedPermissions.includes(key);
  const isGranted = (key: string) => !isDenied(key);

  const toggleModule = (mod: PermissionModule) => {
    const allKeys = getModuleItemKeys(mod);
    const allGranted = allKeys.every(k => isGranted(k));
    if (allGranted) {
      // Deny all
      onChange([...deniedPermissions, ...allKeys.filter(k => !isDenied(k))]);
    } else {
      // Grant all
      onChange(deniedPermissions.filter(k => !allKeys.includes(k)));
    }
  };

  const toggleCategory = (cat: PermissionCategory) => {
    const allKeys = getCategoryItemKeys(cat);
    const allGranted = allKeys.every(k => isGranted(k));
    if (allGranted) {
      onChange([...deniedPermissions, ...allKeys.filter(k => !isDenied(k))]);
    } else {
      onChange(deniedPermissions.filter(k => !allKeys.includes(k)));
    }
  };

  const toggleItem = (key: string) => {
    if (isDenied(key)) {
      onChange(deniedPermissions.filter(k => k !== key));
    } else {
      onChange([...deniedPermissions, key]);
    }
  };

  const getModuleState = (mod: PermissionModule): 'all' | 'none' | 'partial' => {
    const keys = getModuleItemKeys(mod);
    const grantedCount = keys.filter(k => isGranted(k)).length;
    if (grantedCount === keys.length) return 'all';
    if (grantedCount === 0) return 'none';
    return 'partial';
  };

  const getCategoryState = (cat: PermissionCategory): 'all' | 'none' | 'partial' => {
    const keys = getCategoryItemKeys(cat);
    const grantedCount = keys.filter(k => isGranted(k)).length;
    if (grantedCount === keys.length) return 'all';
    if (grantedCount === 0) return 'none';
    return 'partial';
  };

  return (
    <div className="space-y-2">
      {PERMISSION_TREE.map((mod) => {
        const modState = getModuleState(mod);
        const isExpanded = expandedModules.includes(mod.key);

        return (
          <div key={mod.key} className="bg-surface-hover/30 rounded-xl border border-border-subtle overflow-hidden">
            {/* Module header */}
            <div className="flex items-center gap-3 px-4 py-3">
              <button
                type="button"
                onClick={() =>
                  setExpandedModules(prev =>
                    prev.includes(mod.key) ? prev.filter(k => k !== mod.key) : [...prev, mod.key]
                  )
                }
                className="text-text-secondary hover:text-text-primary transition-colors"
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>

              <button
                type="button"
                onClick={() => !disabled && toggleModule(mod)}
                disabled={disabled}
                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                  disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                } ${
                  modState === 'all'
                    ? 'bg-accent-primary border-accent-primary'
                    : modState === 'partial'
                    ? 'bg-accent-primary/30 border-accent-primary/60'
                    : 'border-gray-600 hover:border-gray-400'
                }`}
              >
                {modState === 'all' && (
                  <svg className="w-3 h-3 text-text-primary" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
                {modState === 'partial' && (
                  <div className="w-2 h-0.5 bg-white rounded" />
                )}
              </button>

              <div className="flex items-center gap-2 flex-1">
                <Shield className="w-4 h-4 text-accent-primary" />
                <span className="text-sm font-semibold text-text-primary">{mod.label}</span>
                <span className="text-xs text-text-muted">
                  ({getModuleItemKeys(mod).filter(k => isGranted(k)).length}/{getModuleItemKeys(mod).length})
                </span>
              </div>
            </div>

            {/* Categories */}
            {isExpanded && (
              <div className="border-t border-border-subtle">
                {mod.categories.map((cat) => {
                  const catState = getCategoryState(cat);
                  const isCatExpanded = expandedCategories.includes(cat.key);

                  return (
                    <div key={cat.key} className="border-b border-border-subtle last:border-b-0">
                      {/* Category header */}
                      <div className="flex items-center gap-3 pl-10 pr-4 py-2.5 bg-surface-hover/20">
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedCategories(prev =>
                              prev.includes(cat.key) ? prev.filter(k => k !== cat.key) : [...prev, cat.key]
                            )
                          }
                          className="text-text-secondary hover:text-text-primary transition-colors"
                        >
                          {isCatExpanded ? (
                            <ChevronDown className="w-3.5 h-3.5" />
                          ) : (
                            <ChevronRight className="w-3.5 h-3.5" />
                          )}
                        </button>

                        <button
                          type="button"
                          onClick={() => !disabled && toggleCategory(cat)}
                          disabled={disabled}
                          className={`w-4.5 h-4.5 w-[18px] h-[18px] rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                            disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                          } ${
                            catState === 'all'
                              ? 'bg-accent-primary border-accent-primary'
                              : catState === 'partial'
                              ? 'bg-accent-primary/30 border-accent-primary/60'
                              : 'border-gray-600 hover:border-gray-400'
                          }`}
                        >
                          {catState === 'all' && (
                            <svg className="w-2.5 h-2.5 text-text-primary" viewBox="0 0 12 12" fill="none">
                              <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                          {catState === 'partial' && (
                            <div className="w-1.5 h-0.5 bg-white rounded" />
                          )}
                        </button>

                        <span className="text-sm font-medium text-text-secondary">{cat.label}</span>
                        <span className="text-xs text-text-muted">
                          ({getCategoryItemKeys(cat).filter(k => isGranted(k)).length}/{cat.items.length})
                        </span>
                      </div>

                      {/* Permission items */}
                      {isCatExpanded && (
                        <div className="pl-20 pr-4 py-1 space-y-0.5">
                          {cat.items.map((item) => (
                            <label
                              key={item.key}
                              className={`flex items-center gap-3 py-1.5 rounded-lg px-2 hover:bg-surface-muted/30 transition-colors ${
                                disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                              }`}
                            >
                              <button
                                type="button"
                                onClick={() => !disabled && toggleItem(item.key)}
                                disabled={disabled}
                                className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-all flex-shrink-0 ${
                                  disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                                } ${
                                  isGranted(item.key)
                                    ? 'bg-accent-primary border-accent-primary'
                                    : 'border-gray-600 hover:border-gray-400'
                                }`}
                              >
                                {isGranted(item.key) && (
                                  <svg className="w-2.5 h-2.5 text-text-primary" viewBox="0 0 12 12" fill="none">
                                    <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                )}
                              </button>
                              <span className={`text-xs ${isGranted(item.key) ? 'text-text-secondary' : 'text-text-muted line-through'}`}>
                                {item.label}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── User Form Modal ─────────────────────────────────────────
function UserModal({
  user,
  isOwner,
  onClose,
  onSaved,
}: {
  user: TenantUser | null; // null = create mode
  isOwner: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = !!user;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    email: user?.email || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    password: '',
    role: user?.role || 'EMPLOYEE',
    isActive: user?.isActive ?? true,
  });

  const [deniedPermissions, setDeniedPermissions] = useState<string[]>(
    () => {
      if (!user?.permissions) return [];
      // permissions is stored as JSON - could be array of strings (denied perms)
      const perms = user.permissions;
      if (Array.isArray(perms)) return perms as string[];
      return [];
    }
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      if (isEdit) {
        await userApi.update(user.id, {
          firstName: formData.firstName || undefined,
          lastName: formData.lastName || undefined,
          role: isOwner ? undefined : formData.role,
          isActive: formData.isActive,
          permissions: deniedPermissions,
        });
        setSuccess('Użytkownik został zaktualizowany');
      } else {
        if (!formData.email.trim()) {
          setError('Adres e-mail jest wymagany');
          setSaving(false);
          return;
        }
        if (formData.password.length < 8) {
          setError('Hasło musi mieć co najmniej 8 znaków');
          setSaving(false);
          return;
        }
        await userApi.create({
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName || undefined,
          lastName: formData.lastName || undefined,
          role: formData.role,
        });
        setSuccess('Użytkownik został utworzony');
      }
      setTimeout(() => {
        onSaved();
        onClose();
      }, 500);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Nie udało się zapisać użytkownika');
    } finally {
      setSaving(false);
    }
  };

  // ESC to close
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const totalCount = PERMISSION_TREE.flatMap(m => m.categories.flatMap(c => c.items)).length;
  const grantedItemCount = totalCount - deniedPermissions.filter(k => !k.includes('management.')).length;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-surface-raised rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-bold text-text-primary flex items-center gap-2">
            {isEdit ? (
              <>
                <Shield className="w-6 h-6 text-accent-primary" />
                Edytuj użytkownika
              </>
            ) : (
              <>
                <Plus className="w-6 h-6 text-accent-primary" />
                Nowy użytkownik
              </>
            )}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-text-secondary hover:text-text-primary hover:bg-surface-muted rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div className="flex items-center p-3 bg-danger/10 border border-danger/30 rounded-xl text-danger">
              <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}
          {success && (
            <div className="flex items-center p-3 bg-success/10 border border-success/30 rounded-xl text-success">
              <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="text-sm">{success}</span>
            </div>
          )}

          {/* Basic info */}
          <div className="bg-surface-hover/30 rounded-xl p-4 space-y-4">
            <h3 className="text-sm font-semibold text-text-secondary flex items-center gap-2">
              <User className="w-4 h-4" />
              Dane użytkownika
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-text-secondary mb-1">Imię</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="input w-full"
                  placeholder="Jan"
                />
              </div>
              <div>
                <label className="block text-xs text-text-secondary mb-1">Nazwisko</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="input w-full"
                  placeholder="Kowalski"
                />
              </div>
            </div>

            {!isEdit && (
              <>
                <div>
                  <label className="block text-xs text-text-secondary mb-1">
                    <Mail className="w-3 h-3 inline mr-1" />
                    Adres e-mail <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="input w-full"
                    placeholder="jan@firma.pl"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs text-text-secondary mb-1">
                    Hasło <span className="text-danger">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="input w-full pr-10"
                      placeholder="Min. 8 znaków"
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </>
            )}

            {isEdit && (
              <div>
                <label className="block text-xs text-text-secondary mb-1">
                  <Mail className="w-3 h-3 inline mr-1" />
                  Adres e-mail
                </label>
                <input
                  type="email"
                  value={formData.email}
                  className="input w-full opacity-50"
                  disabled
                />
              </div>
            )}

            <div>
              <label className="block text-xs text-text-secondary mb-1">Rola</label>
              <Select
                value={formData.role}
                onChange={(event) => setFormData({ ...formData, role: event.target.value })}
                disabled={isOwner}
              >
                {isOwner && <option value="SUPER_ADMIN">Właściciel</option>}
                {ASSIGNABLE_ROLES.map(role => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </Select>
              {formData.role === 'MARKETER' && (
                <p className="text-xs text-text-muted mt-1.5">
                  Może tworzyć, generować, testować i publikować materiały niskiego ryzyka. Nie zmienia providera, reguł zgód ani limitów systemowych.
                </p>
              )}
            </div>

            {isEdit && !isOwner && (
              <div className="flex items-center gap-3">
                <label className="text-xs text-text-secondary">Status konta:</label>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                  className={`relative w-10 h-5 rounded-full transition-colors ${
                    formData.isActive ? 'bg-success' : 'bg-gray-600'
                  }`}
                >
                  <div
                    className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                      formData.isActive ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
                <span className={`text-xs ${formData.isActive ? 'text-success' : 'text-text-muted'}`}>
                  {formData.isActive ? 'Aktywny' : 'Nieaktywny'}
                </span>
              </div>
            )}
          </div>

          {/* Permissions */}
          {isEdit && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-text-secondary flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" />
                  Uprawnienia
                  <span className="text-xs text-text-muted font-normal">
                    ({grantedItemCount}/{totalCount} uprawnień)
                  </span>
                </h3>
                {!isOwner && deniedPermissions.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setDeniedPermissions([])}
                    className="text-xs text-accent-primary hover:text-text-primary transition-colors"
                  >
                    Przywróć wszystkie
                  </button>
                )}
              </div>

              {isOwner ? (
                <div className="bg-surface-hover/30 rounded-xl p-4 flex items-center gap-3">
                  <Crown className="w-5 h-5 text-warning" />
                  <div>
                    <p className="text-sm text-text-primary font-medium">Właściciel organizacji</p>
                    <p className="text-xs text-text-secondary">
                      Ten użytkownik ma pełen dostęp do wszystkich funkcji i nie można go ograniczyć.
                    </p>
                  </div>
                </div>
              ) : (
                <PermissionTreeEditor
                  deniedPermissions={deniedPermissions}
                  onChange={setDeniedPermissions}
                />
              )}
            </div>
          )}

          {/* Note for new users */}
          {!isEdit && (
            <div className="bg-surface-hover/30 rounded-xl p-4 flex items-center gap-3">
              <ShieldCheck className="w-5 h-5 text-success flex-shrink-0" />
              <p className="text-xs text-text-secondary">
                Nowy użytkownik otrzyma pełen dostęp do wszystkich funkcji.
                Po utworzeniu konta możesz edytować jego uprawnienia.
              </p>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="btn-primary text-sm"
          >
            {saving ? (
              <span className="flex items-center">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Zapisywanie...
              </span>
            ) : (
              <span className="flex items-center">
                <Save className="w-4 h-4 mr-2" />
                {isEdit ? 'Zapisz zmiany' : 'Utwórz użytkownika'}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
          >
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Users Page ─────────────────────────────────────────
export function UsersPage() {
  const { user: currentUser } = useAuthStore();
  const confirmDialog = useConfirmDialog();
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingUser, setEditingUser] = useState<TenantUser | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const { data } = await userApi.list();
      setUsers(data);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Nie udało się załadować użytkowników');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleDelete = async (userId: string) => {
    if (!(await confirmDialog({
      title: 'Usuń użytkownika',
      message: 'Czy na pewno chcesz usunąć tego użytkownika?',
      confirmLabel: 'Usuń',
    }))) return;
    setDeletingUserId(userId);
    try {
      await userApi.delete(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      alert(error.response?.data?.error || 'Nie udało się usunąć użytkownika');
    } finally {
      setDeletingUserId(null);
    }
  };

  // Find the owner (first created user = SUPER_ADMIN or earliest ADMIN)
  const findOwner = (): string | null => {
    const superAdmin = users.find(u => u.role === 'SUPER_ADMIN');
    if (superAdmin) return superAdmin.id;
    // Fallback: oldest admin
    const admins = users.filter(u => u.role === 'ADMIN').sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    return admins[0]?.id || null;
  };

  const ownerId = findOwner();

  const handleEditUser = async (user: TenantUser) => {
    // Fetch full user details with permissions
    try {
      const { data } = await userApi.get(user.id);
      setEditingUser(data);
    } catch {
      setEditingUser(user);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="loader" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
            <Users className="w-6 h-6 text-accent-primary" />
            Użytkownicy
          </h1>
          <p className="text-text-secondary mt-1">
            Zarządzaj użytkownikami i ich uprawnieniami w organizacji
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary text-sm"
        >
          <Plus className="w-4 h-4 mr-2" />
          Dodaj użytkownika
        </button>
      </div>

      {error && (
        <div className="flex items-center p-4 bg-danger/10 border border-danger/30 rounded-xl text-danger mb-6">
          <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Users table */}
      <div className="card overflow-hidden p-0">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border-subtle">
              <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider bg-surface-raised/80">
                Użytkownik
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider bg-surface-raised/80">
                Rola
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider bg-surface-raised/80">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider bg-surface-raised/80">
                Uprawnienia
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider bg-surface-raised/80">
                Ostatnie logowanie
              </th>
              <th className="px-6 py-4 text-right text-xs font-semibold text-text-secondary uppercase tracking-wider bg-surface-raised/80">
                Akcje
              </th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isOwnerUser = u.id === ownerId;
              const isSelf = u.id === currentUser?.id;
              const deniedCount = Array.isArray(u.permissions) ? (u.permissions as string[]).length : 0;
              const totalPerms = PERMISSION_TREE.flatMap(m => m.categories.flatMap(c => c.items)).length;

              return (
                <tr key={u.id} className="border-b border-border-subtle hover:bg-surface-hover/30 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isOwnerUser ? 'bg-success/20' : 'bg-accent-deep'
                      }`}>
                        {isOwnerUser ? (
                          <Crown className="w-4 h-4 text-success" />
                        ) : (
                          <span className="text-text-primary font-semibold text-sm">
                            {u.firstName?.[0]?.toUpperCase() || u.email[0].toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-text-primary">
                          {u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : u.email}
                          {isSelf && <span className="text-xs text-text-muted ml-2">(Ty)</span>}
                        </p>
                        <p className="text-xs text-text-muted">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      isOwnerUser
                        ? 'bg-success/20 text-success border border-success/30'
                        : deniedCount > 0
                        ? 'bg-danger/20 text-danger border border-danger/30'
                        : 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                    }`}>
                      {getRoleLabel(u.role, isOwnerUser)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${
                      u.isActive ? 'text-success' : 'text-text-muted'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${u.isActive ? 'bg-success' : 'bg-gray-500'}`} />
                      {u.isActive ? 'Aktywny' : 'Nieaktywny'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {isOwnerUser ? (
                      <span className="text-xs text-success">Pełen dostęp</span>
                    ) : deniedCount === 0 ? (
                      <span className="text-xs text-orange-400">Pełen dostęp</span>
                    ) : (
                      <span className="text-xs text-danger">
                        {totalPerms - deniedCount}/{totalPerms}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-xs text-text-muted">
                      {u.lastLoginAt
                        ? new Date(u.lastLoginAt).toLocaleString('pl-PL')
                        : 'Nigdy'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEditUser(u)}
                        className="p-1.5 text-text-secondary hover:text-accent-primary hover:bg-surface-muted rounded-lg transition-all"
                        title="Edytuj"
                      >
                        <Shield className="w-4 h-4" />
                      </button>
                      {!isOwnerUser && !isSelf && (
                        <button
                          onClick={() => handleDelete(u.id)}
                          disabled={deletingUserId === u.id}
                          className="p-1.5 text-text-secondary hover:text-danger hover:bg-danger/10 rounded-lg transition-all disabled:opacity-50"
                          title="Usuń"
                        >
                          {deletingUserId === u.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {users.length === 0 && !loading && (
          <EmptyState
            icon={Users}
            title="Brak użytkowników"
          />
        )}
      </div>

      {/* Edit modal */}
      {editingUser && (
        <UserModal
          user={editingUser}
          isOwner={editingUser.id === ownerId}
          onClose={() => setEditingUser(null)}
          onSaved={loadUsers}
        />
      )}

      {/* Create modal */}
      {showCreateModal && (
        <UserModal
          user={null}
          isOwner={false}
          onClose={() => setShowCreateModal(false)}
          onSaved={loadUsers}
        />
      )}
    </div>
  );
}
